/**
 * Longest summary we will hand back, in characters.
 *
 * "First paragraph" is not a length bound. Tool descriptions are written for a
 * model, and some run to hundreds of words on a single line, complete with
 * worked JSON examples; for those, the first line is the whole thing.
 */
const SUMMARY_MAX_LENGTH = 240;

/** Cut at the last sentence end that fits, else the last whole word. */
function trimToLength(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  const window = text.slice(0, maxLength);

  // A break is only worth taking if it leaves a usable summary. Both of these
  // can land near the start — "e.g. " gives an early full stop, and a long
  // unbroken run gives an early final space — and cutting there returns a stub.
  const minUseful = maxLength * 0.4;

  const sentenceEnd = Math.max(
    window.lastIndexOf('. '),
    window.lastIndexOf('! '),
    window.lastIndexOf('? ')
  );
  if (sentenceEnd > minUseful) {
    return window.slice(0, sentenceEnd + 1);
  }

  const wordEnd = window.lastIndexOf(' ');
  const cut = wordEnd > minUseful ? window.slice(0, wordEnd) : window;
  return `${cut.trimEnd()}…`;
}

/** Strip markdown title and extract the first plain-text paragraph as a summary. */
export function extractDescriptionSummary(description: string): string {
  const lines = description?.split('\n') ?? [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (
      trimmed === '' ||
      trimmed.startsWith('#') ||
      trimmed.startsWith('**Operation') ||
      trimmed === '---'
    ) {
      continue;
    }
    return trimToLength(trimmed, SUMMARY_MAX_LENGTH);
  }
  return trimToLength(description ?? '', SUMMARY_MAX_LENGTH);
}

/**
 * Generates HTML for a tool mention that can be pasted into TipTap
 * and automatically parsed as a mention node.
 */
export function generateToolMentionHtml(
  toolKey: string,
  displayName: string
): string {
  const mentionData = JSON.stringify({
    t: 'tool',
    name: toolKey,
    data: {
      toolId: toolKey,
      toolName: toolKey,
    },
  });

  return `<span data-type="mention" data-id="${toolKey}" data-label="${displayName}" data-color="purple" data-data='${mentionData}' data-reference-id="${toolKey}">@${displayName}</span>`;
}

/**
 * Display labels for credential strategies. `short` suits pills and table
 * cells; `long` suits the toggle that picks between strategies.
 *
 * Lives here rather than in the tool-connections constants so that consumers
 * get the labels without pulling in that module's provider-logo barrel.
 */
export const AUTH_STRATEGY_LABELS: Record<
  string,
  { short: string; long: string }
> = {
  apikey: { short: 'API Key', long: 'API Key' },
  api_key: { short: 'API Key', long: 'API Key' },
  oauth: { short: 'OAuth', long: 'OAuth' },
  oauth2: { short: 'OAuth', long: 'OAuth' },
  user_password: { short: 'Password', long: 'Username & Password' },
  user_managed: { short: 'API Key', long: 'API Key' },
  user_provided: { short: 'API Key', long: 'API Key' },
  workos_pipe: { short: 'OAuth', long: 'OAuth' },
  salesforce_auth: { short: 'OAuth', long: 'OAuth' },
  slack_oauth: { short: 'OAuth', long: 'OAuth' },
  linear_oauth: { short: 'OAuth', long: 'OAuth' },
  // Zendesk: `long` must equal the server strategy's `displayName` — the
  // connection modal resolves the selected toggle label back to a strategy by
  // that match, and an unmatched label silently falls back to strategies[0].
  zendesk_oauth: { short: 'OAuth', long: 'OAuth' },
  zendesk_apikey: { short: 'API Key', long: 'API Key' },
  slack_auth: { short: 'Coco', long: 'Install Coco' },
  slack_search_coco_install: {
    short: 'Coco',
    long: 'Install Coco in Slack',
  },
  platform_managed: { short: 'Built-in', long: 'Cotera Built-in' },
  none: { short: 'No Auth', long: 'No Auth' },
};

/** Title-cases an unmapped strategy name, e.g. `user_managed` -> `User Managed`. */
export function formatStrategyName(name: string): string {
  return name
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Display label for an auth strategy.
 * @param variant - 'short' for pills, 'long' for toggle labels
 */
export function getAuthStrategyLabel(
  name: string,
  variant: 'short' | 'long'
): string {
  const labels = AUTH_STRATEGY_LABELS[name];
  return labels?.[variant] ?? formatStrategyName(name);
}
